import { GalleryHorizontalEnd, Grid3X3 } from 'lucide-react'
import { SplitKind, type SplitSettings } from '@/core/document'
import { useEditSession } from '@/core/store/editSession'
import { PREVIEW_OLDER_POST_OFFSETS } from '@/core/store/uiState'
import { TEST_IDS } from '@/lib/testIds'
import { cn } from '@/lib/utils'
import { PreviewCanvas } from '@/tools/preview/PreviewCanvas'
import type { PreviewTile } from '@/tools/preview/renderPreviewTiles'

interface ProfilePreviewProps {
  tiles: readonly PreviewTile[]
  split: SplitSettings | null
}

const OFFSET_LABELS: Readonly<Record<(typeof PREVIEW_OLDER_POST_OFFSETS)[number], string>> = {
  0: 'Newest',
  1: 'After 1 post',
  2: 'After 2 posts',
}

export function ProfilePreview({ tiles, split }: ProfilePreviewProps) {
  const olderOffset = useEditSession((state) => state.previewOlderPostOffset)
  const setOlderOffset = useEditSession((state) => state.setPreviewOlderPostOffset)
  const gridSplit = split?.kind === SplitKind.Grid
  const tileByPosition = new Map<number, PreviewTile>()
  for (const tile of tiles) {
    const position = olderOffset + tile.row * 3 + tile.column
    if (tileByPosition.has(position)) throw new Error(`Preview profile position ${position} contains duplicate uploads`)
    tileByPosition.set(position, tile)
  }
  const cellCount = Math.max(12, olderOffset + (gridSplit ? tiles.length : 1))
  const helper = gridSplit
    ? 'Grid splits line up only while these posts stay together. One newer post shifts every tile and breaks the picture.'
    : split?.kind === SplitKind.Carousel
      ? 'Only the first carousel slide appears on your profile grid.'
      : 'Your profile shows the centered 3:4 area of this post.'

  return (
    <div className="min-h-0 flex-1 overflow-y-auto bg-background" data-testid={TEST_IDS.previewProfile}>
      <div className="mx-auto w-full max-w-[390px]">
        <div className="flex items-center gap-5 px-5 py-5">
          <div className="size-20 shrink-0 rounded-full bg-gradient-to-br from-muted-foreground/30 to-muted" aria-label="Profile picture placeholder" />
          <div className="min-w-0 flex-1">
            <p className="font-semibold">your_name</p>
            <div className="mt-3 grid grid-cols-3 gap-3 text-center text-xs">
              <span><strong className="block text-sm">12</strong>posts</span>
              <span><strong className="block text-sm">—</strong>followers</span>
              <span><strong className="block text-sm">—</strong>following</span>
            </div>
          </div>
        </div>

        <div className="border-y p-3">
          <p className="text-xs text-muted-foreground">{helper}</p>
          <fieldset className="mt-3">
            <legend className="text-xs font-medium">Show as older post</legend>
            <div className="mt-2 grid grid-cols-3 rounded-lg bg-muted p-1">
              {PREVIEW_OLDER_POST_OFFSETS.map((offset) => (
                <button
                  key={offset}
                  type="button"
                  aria-pressed={olderOffset === offset}
                  data-testid={TEST_IDS.previewOlderOffset(offset)}
                  onClick={() => setOlderOffset(offset)}
                  className="rounded-md px-2 py-1.5 text-[11px] font-medium text-muted-foreground aria-pressed:bg-background aria-pressed:text-foreground aria-pressed:shadow-sm"
                >
                  {OFFSET_LABELS[offset]}
                </button>
              ))}
            </div>
          </fieldset>
        </div>

        <div className="flex h-11 items-center justify-center border-b text-foreground" aria-hidden="true">
          <Grid3X3 className="size-5" />
        </div>
        <div className="grid grid-cols-3 gap-0.5 bg-background" aria-label="Profile post grid">
          {Array.from({ length: cellCount }, (_, position) => {
            const tile = tileByPosition.get(position)
            return tile ? (
              <div key={position} className="relative aspect-[3/4] overflow-hidden bg-muted">
                <PreviewCanvas
                  source={tile.postCanvas}
                  sourceRect={tile.gridRect}
                  testId={TEST_IDS.previewProfileTile(tile.uploadNumber)}
                  uploadNumber={tile.uploadNumber}
                  className="size-full object-cover"
                  ariaLabel={`Preview upload ${tile.uploadNumber} on profile grid`}
                />
                {tile.slides.length > 1 ? (
                  <GalleryHorizontalEnd className="absolute right-1.5 top-1.5 size-4 text-white drop-shadow" data-testid={TEST_IDS.previewCarouselMarker} aria-label="Carousel post" />
                ) : null}
              </div>
            ) : <div key={position} className={cn('aspect-[3/4] bg-muted', position % 4 === 0 && 'bg-muted-foreground/15')} aria-hidden="true" />
          })}
        </div>
      </div>
    </div>
  )
}
